import { css } from '@emotion/css';
import { Box, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Settings04Icon } from '@/components/icons/Settings04Icon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { Card } from '@/components/layout/Card/Card';
import { Modal } from '@/components/modals/Modal/Modal';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import {
  DispositiveProvisionDispositionKind,
  TransferMethod,
} from '@/types/schema';

import { otherDispositionKindOptions } from '../DispositiveProvisionsForm.constants';
import { getDispositionKindOption } from '../DispositiveProvisionsFormRecipients/DispositiveProvisionsRecipient.utils';
import { useRemainingExemptionAmountForClient } from '../hooks/useRemainingExemptionAmountForClient';
import { SplitAdvancedSettingsCard } from './SplitAdvancedSettingsCard';
import {
  SPLIT_DISTRIBUTION_KIND_OPTIONS,
  SplitDispositionFormShape,
  SplitDispositionModalResult,
} from './SplitDispositionModal.types';
import { createSplitRecipients } from './SplitDispositionModal.utils';
import { SplitRecipientsTable } from './SplitRecipientsTable';

const dynamicHeightStyle = css({
  height: 'auto',
  maxHeight: '80vh',
});

export interface SplitModalShellProps {
  isOpen: boolean;
  onClose: (result: SplitDispositionModalResult | null) => void;
  hasStateTax?: boolean;
  /**
   * Whether the recipients table offers state-specific recipient *type*
   * options. Defaults to `hasStateTax`. Beneficiary designations never support
   * state-specific recipient types (their recipient card hardcodes this off),
   * so the BDG modal passes `false` here while still letting `hasStateTax`
   * drive the state-specific formula options.
   */
  recipientTypeHasStateTax?: boolean;
  /** Heading text — varies between dispositions and beneficiary designations. */
  heading: string;
  /**
   * Remaining-exemption data used to annotate the formula options. Pass
   * `undefined` when there is no death-order grantor in scope (e.g. account
   * beneficiary designations).
   */
  clientExemptionData: ReturnType<
    typeof useRemainingExemptionAmountForClient
  >['data'];
  /** Recipient multi-select; the host supplies the data source. */
  renderRecipientSelector: (errorMessage: string | undefined) => ReactNode;
  /** Asset multi-select shown when distributionKind === 'asset'. */
  renderAssetSelector: () => ReactNode;
}

/**
 * Shared body for the "evenly split" modal. The recipient and asset selectors
 * are injected so this shell can back both the dispositive-provisions form and
 * the beneficiary-designations form, which source recipients/assets
 * differently.
 */
export function SplitModalShell({
  isOpen,
  onClose,
  hasStateTax = false,
  recipientTypeHasStateTax,
  heading,
  clientExemptionData,
  renderRecipientSelector,
  renderAssetSelector,
}: SplitModalShellProps) {
  const { control, handleSubmit, setValue, getValues, setError, clearErrors } =
    useFormContext<SplitDispositionFormShape>();

  const distributionKind = useWatch({ control, name: 'distributionKind' });
  const watchedRecipients = useWatch({ control, name: 'selectedRecipients' });
  const selectedRecipients = useMemo(
    () => watchedRecipients ?? [],
    [watchedRecipients]
  );
  const hasRecipients = selectedRecipients.length > 0;

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [recipientError, setRecipientError] = useState('');

  const prevDistributionKind = usePrevious(distributionKind);

  // Reset distribution value and formula kind when distribution kind changes
  useEffect(() => {
    if (prevDistributionKind && distributionKind !== prevDistributionKind) {
      setValue('distributionValue', null);
      setValue('formulaKind', '');
      clearErrors('distributionValue');
      clearErrors('formulaKind');
      clearErrors('dispositionAssetPercentage');

      // Max distribution amount is only supported for percentage distributions
      if (distributionKind !== 'percentage') {
        setValue('maxDistributionAmount', null);
      }

      // Set asset defaults when switching to asset, only if no existing values
      if (distributionKind === 'asset') {
        if (getValues('dispositionAssetPercentage') == null) {
          setValue('dispositionAssetPercentage', new Decimal(100));
        }
        if (!getValues('transferMethod')) {
          setValue('transferMethod', TransferMethod.InKind);
        }
      }

      // Hide advanced settings when switching to non-percentage and non-asset
      if (distributionKind !== 'percentage' && distributionKind !== 'asset') {
        setShowAdvancedSettings(false);
      }
    }
  }, [
    distributionKind,
    prevDistributionKind,
    setValue,
    getValues,
    clearErrors,
  ]);

  const watchedAssetIds = useWatch({
    control,
    name: [
      'assetGroupIds',
      'assetStaticIds',
      'insurancePolicyIds',
      'ownershipStakeIds',
    ],
  });

  // Clear validation error when recipients or asset selection changes
  useEffect(() => {
    setRecipientError('');
  }, [selectedRecipients, watchedAssetIds]);

  const formulaOptions = useMemo(
    () =>
      otherDispositionKindOptions.flatMap((option) => {
        // Skip state-specific options if no state tax
        if (!hasStateTax) {
          if (
            [
              DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption,
              DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption,
              DispositiveProvisionDispositionKind.RemainingStateExemption,
            ].includes(option.value)
          ) {
            return [];
          }
        }

        return getDispositionKindOption(option, clientExemptionData, []);
      }),
    [clientExemptionData, hasStateTax]
  );

  const onSubmit = useCallback(
    (formValues: SplitDispositionFormShape) => {
      let hasErrors = false;

      // Validate recipients
      const recipientErrors: string[] = [];
      if (formValues.selectedRecipients.length < 2) {
        recipientErrors.push('Please select at least 2 recipients');
      }
      const missingTransferTaxKind = formValues.selectedRecipients.some(
        (r) => !r.transferTaxKind
      );
      if (missingTransferTaxKind) {
        recipientErrors.push(
          'Please select a recipient type for all recipients'
        );
      }
      // Validate formula kind
      if (
        formValues.distributionKind === 'formula' &&
        !formValues.formulaKind
      ) {
        setError('formulaKind', { message: 'Please select a formula' });
        hasErrors = true;
      }

      // Validate distribution value for amount
      if (
        formValues.distributionKind === 'amount' &&
        (!formValues.distributionValue ||
          formValues.distributionValue.lessThanOrEqualTo(0))
      ) {
        setError('distributionValue', {
          message: 'Please enter a valid amount',
        });
        hasErrors = true;
      }

      // Validate distribution value for percentage
      if (
        formValues.distributionKind === 'percentage' &&
        (!formValues.distributionValue ||
          formValues.distributionValue.lessThanOrEqualTo(0) ||
          formValues.distributionValue.greaterThan(100))
      ) {
        setError('distributionValue', {
          message: 'Please enter a valid percentage (1-100)',
        });
        hasErrors = true;
      }

      // Validate asset selection
      if (formValues.distributionKind === 'asset') {
        const totalAssets =
          formValues.assetGroupIds.length +
          formValues.assetStaticIds.length +
          formValues.insurancePolicyIds.length +
          formValues.ownershipStakeIds.length;
        if (totalAssets === 0) {
          recipientErrors.push('Please select at least one asset');
        }

        // Validate asset percentage
        if (
          !formValues.dispositionAssetPercentage ||
          formValues.dispositionAssetPercentage.lessThanOrEqualTo(0) ||
          formValues.dispositionAssetPercentage.greaterThan(100)
        ) {
          setError('dispositionAssetPercentage', {
            message: 'Please enter a valid percentage',
          });
          hasErrors = true;
        }
      }

      // Flush all accumulated recipient/asset errors at once
      if (recipientErrors.length > 0) {
        setRecipientError(recipientErrors.join('. '));
        hasErrors = true;
      }

      if (hasErrors) return;

      const recipients = createSplitRecipients(formValues);
      onClose({ recipients });
    },
    [onClose, setError]
  );

  const handleCancel = useCallback(() => {
    onClose(null);
  }, [onClose]);

  const handleToggleAdvancedSettings = useCallback(() => {
    setShowAdvancedSettings((prev) => !prev);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      heading={heading}
      onClose={handleCancel}
      classes={{ paper: dynamicHeightStyle }}
      actions={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit(onSubmit)}
            disabled={selectedRecipients.length < 2}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        {/* Disposition Details Card */}
        <Card variant="filled" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="title-s">Disposition details</Typography>

            <Stack direction="row" spacing={2} alignItems="flex-end">
              <Box sx={{ width: 220 }}>
                <FormAwareButtonGroup<SplitDispositionFormShape>
                  label="Distribution type"
                  fieldName="distributionKind"
                  control={control}
                  options={SPLIT_DISTRIBUTION_KIND_OPTIONS}
                  variant="light"
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {distributionKind === 'percentage' && (
                      <FormAwarePercentInput<SplitDispositionFormShape>
                        control={control}
                        fieldName="distributionValue"
                        label="Total distribution percentage"
                        isDecimalJSInput
                        decimalScale={5}
                        fixedDecimalScale={false}
                      />
                    )}

                    {distributionKind === 'amount' && (
                      <FormAwareCurrencyInput<SplitDispositionFormShape>
                        control={control}
                        fieldName="distributionValue"
                        label="Total distribution amount"
                        isDecimalJSInput
                      />
                    )}

                    {distributionKind === 'formula' && (
                      <FormAwareSelectInput<SplitDispositionFormShape>
                        control={control}
                        fieldName="formulaKind"
                        label="Disposition"
                        hideLabel
                        options={formulaOptions}
                      />
                    )}

                    {distributionKind === 'asset' && renderAssetSelector()}
                  </Box>
                  {(distributionKind === 'percentage' ||
                    distributionKind === 'asset') && (
                    <IconButton
                      icon={Settings04Icon}
                      ariaLabel="Advanced settings"
                      size="sm"
                      variant="transparent"
                      onClick={handleToggleAdvancedSettings}
                      sx={{
                        backgroundColor: showAdvancedSettings
                          ? COLORS.NAVY[100]
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: COLORS.NAVY[100],
                          borderColor: 'transparent',
                        },
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Advanced Settings */}
            <SplitAdvancedSettingsCard
              isExpanded={showAdvancedSettings}
              distributionKind={distributionKind}
            />
          </Stack>
        </Card>

        {/* Recipients Section */}
        <Card
          variant="outlined"
          sx={{
            ...(hasRecipients && {
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundColor: 'transparent',
            }),
            ...(!hasRecipients && { p: 2 }),
          }}
        >
          {!hasRecipients && (
            <ActionCard
              variant="noCard"
              heading="Recipients"
              description="Distribution above will be evenly split between recipients"
            />
          )}

          {hasRecipients && (
            <Stack spacing={1}>
              <Stack spacing={0.25}>
                <Typography variant="title-s">Recipients</Typography>
                <Typography variant="body-xs">
                  Distribution above will be evenly split between recipients
                  below
                </Typography>
              </Stack>

              <SplitRecipientsTable
                hasStateTax={recipientTypeHasStateTax ?? hasStateTax}
              />
            </Stack>
          )}

          <Box
            sx={{
              width: '100%',
              mt: 2,
            }}
          >
            {renderRecipientSelector(recipientError || undefined)}
          </Box>
        </Card>
      </Stack>
    </Modal>
  );
}
